import React, { useState, useMemo } from 'react';
import {
    Autocomplete,
    TextField,
    Box,
    Typography,
} from '@mui/material';
import { countries, Country } from '../data/countries';

interface CountrySelectorProps {
    value: string;
    onChange: (value: string) => void;
    label?: string;
    placeholder?: string;
    required?: boolean;
    error?: boolean;
    helperText?: string;
    disabled?: boolean;
    sx?: any;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
    value = '',
    onChange,
    label = 'País',
    placeholder = 'Selecciona un país',
    required = false,
    error = false,
    helperText,
    disabled = false,
    sx,
}) => {
    const [inputValue, setInputValue] = useState('');

    // Encontrar el país seleccionado
    const selectedCountry = useMemo(() => {
        return countries.find(country => country.name === value) || null;
    }, [value]);

    // Filtrar países basado en el input
    const filteredCountries = useMemo(() => {
        if (!inputValue) return countries;
        
        const searchTerm = inputValue.toLowerCase();
        return countries.filter(country => 
            country.name.toLowerCase().includes(searchTerm) ||
            country.code.toLowerCase().includes(searchTerm)
        );
    }, [inputValue]);

    const handleChange = (event: any, newValue: Country | null) => {
        onChange(newValue?.name || '');
    };

    const handleInputChange = (event: any, newInputValue: string) => {
        setInputValue(newInputValue);
    };

    return (
        <Autocomplete
            value={selectedCountry}
            onChange={handleChange}
            inputValue={inputValue}
            onInputChange={handleInputChange}
            options={filteredCountries}
            getOptionLabel={(option) => `${option.flag} ${option.name}`}
            isOptionEqualToValue={(option, value) => option.code === value.code}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    required={required}
                    error={error}
                    helperText={helperText}
                    disabled={disabled}
                    variant="outlined"
                    sx={sx}
                />
            )}
            renderOption={(props, option) => {
                const { key, ...otherProps } = props;
                return (
                    <Box component="li" key={key} {...otherProps}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1">{option.flag}</Typography>
                            <Box>
                                <Typography variant="body1">{option.name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {option.code}
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                );
            }}
            filterOptions={(x) => x} // Deshabilitar filtrado automático ya que lo hacemos manualmente
            noOptionsText="No se encontraron países"
            loadingText="Cargando países..."
            clearText="Limpiar"
            openText="Abrir"
            closeText="Cerrar"
            sx={sx}
        />
    );
};

export default CountrySelector; 